<template>
  <ModalDialog
    v-model="isShow"
    :targetClass="isEmbed ? 'submit-button' : null"
    :level="10"
    @close="closeErrorDialog()"
  >
    <div class="p-5" slot="dialog-content">
      <div
        v-if="innerHtml"
        v-html="formatContent(innerHtml, ...params)"
        @click="handleClickContent($event)"
      ></div>
      <h2 v-if="!innerHtml && title">{{ formatContent(title, ...params) }}</h2>
      <h4 class="error-message main-message" v-if="!innerHtml && message">
        <p>{{ formatContent(message, ...params) }}</p>
      </h4>
      <div v-if="errorObj && typeof errorObj !== 'object'">
        {{ extractErrorContent(errorObj) }}
      </div>
      <div v-if="errorObj && typeof errorObj === 'object'">
        <div
          class="error-section"
          v-for="(key, index) of Object.keys(errorObj)"
          :key="index"
        >
          <h4 class="error-message">{{ getDisplayKey(key) }}</h4>
          <div v-if="Array.isArray(errorObj[key])">
            <li
              v-for="(value, valIndex) of errorObj[key]"
              :key="'val-' + valIndex"
            >
              <span v-html="value"></span>
            </li>
          </div>
          <div v-else>
            <li>{{ errorObj[key] }}</li>
          </div>
        </div>
      </div>
      <hr />
      <button
        class="mylisting-button secondary-button"
        @click="closeErrorDialog()"
        style=""
      >
        {{ closeLabel }}
      </button>
    </div>
  </ModalDialog>
</template>
<script>
/* This component is used to show error dialog.
To show an error, call this.showErrorDialog(errorObject)
Structure of errorObject:
{
    title: 'String',
    message: 'String',
    closeLabel: 'String', <- This field is optional, if not set, use default: Close
    onCloseCallback: function reference <- This field is optional, if set, the function will be called after close dialog
    onCloseCallbacData: object <- This field is optional, it is parameter of onCloseCallback function
}

*/
import { REGEX_EXTRACT_HTML_TAG_CONTENT } from "../../../constants/regex";
import ModalDialog from "./ModalDialog";
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "ErrorDialog",
  data: function () {
    return {
      title: "",
      message: "",
      errorObj: null,
      closeLabel: "",
      onCloseCallback: null,
      onCloseCallbackData: null,
      isShow: false,
      params: [],
      innerHtml: null,
      onClickContent: null,
    };
  },
  components: {
    ModalDialog,
  },
  computed: {
    ...mapGetters(["agencyID", "userID"]),
    /* Check if the app is displayed inside an iframe or embed tag
    https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t
    */
    isEmbed() {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
  },
  methods: {
    handleClickContent(event) {
      if (this.onClickContent) {
        this.onClickContent(event.target && event.target.id);
      }
    },
    extractErrorContent(content) {
      if (typeof content !== "string") {
        return content;
      }
      const matchArr = content.match(REGEX_EXTRACT_HTML_TAG_CONTENT);
      if (matchArr != null) {
        return matchArr[1];
      }
      return content;
    },
    closeErrorDialog() {
      this.isShow = false;
      if (this.onCloseCallback) {
        if (!this.onCloseCallbackData) {
          this.onCloseCallback();
        } else {
          this.onCloseCallback(this.onCloseCallbackData);
        }
      }
      this.showErrorDialog(null);
    },
    showError(errorDialog) {
      this.title = errorDialog.title;
      this.message = errorDialog.message;
      this.errorObj = errorDialog.errorObj;
      this.innerHtml = errorDialog.innerHtml;
      if (errorDialog.messageList) {
        this.messageList = errorDialog.messageList;
      }
      if (!errorDialog.closeLabel) {
        this.closeLabel = this.getLabelByKeys("BUTTONS.CLOSE");
      } else {
        this.closeLabel = errorDialog.closeLabel;
      }

      this.params = errorDialog.params ? errorDialog.params : [];
      this.onCloseCallback = errorDialog.onCloseCallback;
      this.onCloseCallbackData = errorDialog.onCloseCallbackData;
      this.onClickContent = errorDialog.onClickContent;
      const key = errorDialog.key;
      let isMultiLanguageCloseLabel = false;
      if (key) {
        const title = this.getLabelByKeys(`ERROR_DIALOGS.${key}.TITLE`);
        if (title) {
          this.title = title;
        }
        const message = this.getLabelByKeys(`ERROR_DIALOGS.${key}.MESSAGE`);
        if (message) {
          this.message = message;
        }
        const innerHtml = this.getLabelByKeys(
          `ERROR_DIALOGS.${key}.INNER_HTML`
        );
        if (innerHtml) {
          this.innerHtml = innerHtml;
        }
        const closeLabel = this.getLabelByKeys(
          `ERROR_DIALOGS.${key}.CLOSE_LABEL`
        );
        if (closeLabel) {
          this.closeLabel = closeLabel;
          isMultiLanguageCloseLabel = true;
        }
      }
      // Use multi-language for close label
      if (!isMultiLanguageCloseLabel) {
        const displayCloseLabel = this.getLabelByKeys(
          `BUTTONS.${this.closeLabel.toUpperCase()}`
        );
        if (displayCloseLabel) {
          this.closeLabel = displayCloseLabel;
        }
      }
      this.isShow = true;
      const requestBody = {
        agency: this.agencyID,
        user: this.userID,
        url: this.$route.query.page,
      };
      if (this.innerHtml) {
        requestBody.full_error = this.formatContent(
          this.innerHtml,
          ...this.params
        );
      } else {
        let fullError = "";
        if (this.title) {
          requestBody.title = this.formatContent(this.title, ...this.params);
          fullError += requestBody.title + "\n";
        }
        if (this.message) {
          requestBody.message = this.formatContent(
            this.message,
            ...this.params
          );
          fullError += requestBody.message + "\n";
        }
        requestBody.full_error = fullError;
      }
      if (this.errorObj) {
        requestBody.full_error += this.tryGet(() =>
          JSON.stringify(this.errorObj)
        );
      }
      axios.post("/error-log/?in_background=true", requestBody);
    },
    getDisplayKey(key) {
      if (key === "reply_address") {
        return "email address";
      } else {
        return key;
      }
    },
  },
  created() {
    this.$store.watch(
      (state) => state.dialogs.errorDialog,
      (value) => {
        if (this.$store.getters.loadingReqNumber > 0) {
          return;
        }
        if (!value) {
          this.isShow = false;
          this.showErrorDialog(null);
        } else {
          this.showError(value);
        }
      },
      { immediate: true }
    );

    this.$store.watch(
      (state) => state.dialogs.loadingReqNumber,
      (value) => {
        if (value > 0) {
          return;
        }
        const errorDialog = this.$store.getters.errorDialog;
        if (errorDialog == null) {
          return;
        }
        this.showError(errorDialog);
      },
      { immediate: true }
    );
  },
};
</script>

<style scoped>
.error-section {
  margin-top: 0.5rem;
}

.error-message {
  text-transform: none;
}

.main-message {
  white-space: pre-line;
}

@media (max-width: 768px) {
  h2 {
    font-size: 24px;
  }

  h4 {
    font-size: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/global-dialogs/ErrorDialog.vue